import { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financeApi, Budget } from '../../api/finance';
import { projectsApi } from '../../api/projects';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = { draft: 'Draft', approved: 'Approved', closed: 'Closed' };

interface FormValues {
  projectId: string; fiscalYearId: string; currencyCode: string;
  lines: { accountId: string; description: string; budgetedAmount: string }[];
}

export function BudgetsPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const budgetsQuery = useQuery({ queryKey: ['finance-budgets'], queryFn: () => financeApi.listBudgets() });
  const projectsQuery = useQuery({ queryKey: ['projects-list'], queryFn: () => projectsApi.list(), enabled: showForm });
  const fiscalYearsQuery = useQuery({ queryKey: ['finance-fiscal-years'], queryFn: () => financeApi.listFiscalYears(), enabled: showForm });
  const accountsQuery = useQuery({ queryKey: ['finance-accounts'], queryFn: () => financeApi.listAccounts(), enabled: showForm });

  const { register, control, handleSubmit, reset } = useForm<FormValues>({ defaultValues: { currencyCode: 'NGN', lines: [{ accountId: '', description: '', budgetedAmount: '' }] } });
  const linesArray = useFieldArray({ control, name: 'lines' });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['finance-budgets'] });

  const createMutation = useMutation({
    mutationFn: (v: FormValues) => financeApi.createBudget({
      projectId: Number(v.projectId), fiscalYearId: Number(v.fiscalYearId), currencyCode: v.currencyCode,
      lines: v.lines.map((l) => ({ accountId: Number(l.accountId), description: l.description || undefined, budgetedAmount: Number(l.budgetedAmount) })),
    }),
    onSuccess: (result) => { showSuccess(`Budget created - ${result.data.budgetReference}`); invalidate(); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create budget.'),
  });

  const approveMutation = useMutation({ mutationFn: (id: number) => financeApi.approveBudget(id), onSuccess: () => { showSuccess('Budget approved.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const closeMutation = useMutation({ mutationFn: (id: number) => financeApi.closeBudget(id), onSuccess: () => { showSuccess('Budget closed.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  return (
    <div className="page">
      <PageHeader title="Budgets" actions={<button className="btn-primary" onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'New Budget'}</button>} />
      <p className="muted">One budget per project per fiscal year, tied to real Chart of Accounts entries - view Budget vs Actual on the project's own Finance tab.</p>

      {showForm && (
        <section className="card">
          <h2>New Budget</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>
              Project
              <select {...register('projectId', { required: true })}>
                <option value="">- select -</option>
                {projectsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
              </select>
            </label>
            <label>
              Fiscal Year
              <select {...register('fiscalYearId', { required: true })}>
                <option value="">- select -</option>
                {fiscalYearsQuery.data?.data.map((fy) => <option key={fy.id} value={fy.id}>{fy.year_label}</option>)}
              </select>
            </label>
            <label>Currency<input {...register('currencyCode')} placeholder="NGN" /></label>

            <h3>Budget Lines</h3>
            {linesArray.fields.map((field, i) => (
              <div key={field.id} className="p11-repeat-block">
                <label>
                  Account
                  <select {...register(`lines.${i}.accountId`, { required: true })}>
                    <option value="">- select -</option>
                    {accountsQuery.data?.data.filter((a) => a.posting_allowed).map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
                  </select>
                </label>
                <label>Description (optional)<input {...register(`lines.${i}.description`)} /></label>
                <label>Budgeted Amount<input type="number" step="0.01" {...register(`lines.${i}.budgetedAmount`, { required: true })} /></label>
                {linesArray.fields.length > 1 && <button type="button" className="link-button" onClick={() => linesArray.remove(i)}>Remove Line</button>}
              </div>
            ))}
            <button type="button" onClick={() => linesArray.append({ accountId: '', description: '', budgetedAmount: '' })}>+ Add Line</button>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create Draft'}</button>
              <button type="button" className="btn-secondary" onClick={() => { setShowForm(false); reset(); }}>Cancel</button>
            </div>
          </form>
        </section>
      )}

      <div className="panel">
        <SectionHeader title="All Budgets" />
        <div className="panel-body">
        {budgetsQuery.data && (
          <DataTable<Budget>
            rows={budgetsQuery.data.data}
            rowKey={(b) => b.id}
            emptyMessage="No budgets yet."
            columns={[
              { header: 'Reference', render: (b) => b.budget_reference },
              { header: 'Project', render: (b) => b.project_name ?? '-' },
              { header: 'Fiscal Year', render: (b) => b.year_label ?? '-' },
              { header: 'Total', render: (b) => `${b.currency_code} ${Number(b.total_amount).toLocaleString()}` },
              { header: 'Status', render: (b) => <StatusBadge status={b.status} label={STATUS_LABELS[b.status]} /> },
              {
                header: '',
                render: (b) => (
                  <span className="row-actions">
                    {b.status === 'draft' && <button className="link-button" onClick={() => approveMutation.mutate(b.id)}>Approve</button>}
                    {b.status === 'approved' && <button className="link-button" onClick={() => closeMutation.mutate(b.id)}>Close</button>}
                  </span>
                ),
              },
            ]}
          />
        )}
      </div>
      </div>
    </div>
  );
}
